import React, { useState } from 'react';
import { Outlet, NavLink, useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import {
  LayoutDashboard, Tv, Package, Users, Monitor,
  CreditCard, Wallet, Shield, Settings, LogOut,
  Menu, X, ChevronRight, Bell, UserCheck, Film, Eye, Megaphone
} from 'lucide-react';

const allNavItems = [
  { path: '/', icon: LayoutDashboard, label: 'Dashboard', exact: true, roles: ['superadmin','distributor','lco'] },
  { path: '/channels', icon: Tv, label: 'Channels', roles: ['superadmin','distributor'] },
  { path: '/packages', icon: Package, label: 'Packages', roles: ['superadmin','distributor','lco'] },
  { path: '/ads', icon: Megaphone, label: 'Ads', roles: ['superadmin','distributor','lco'] },
  { path: '/movies', icon: Film, label: 'Movies / VOD', roles: ['superadmin'] },
  { path: '/users', icon: Users, label: 'Users', roles: ['superadmin','distributor','lco'] },
  { path: '/devices', icon: Monitor, label: 'Devices', roles: ['superadmin','distributor','lco'] },
  { path: '/live-viewers', icon: Eye, label: 'Live Viewers', roles: ['superadmin','distributor','lco'] },
  { path: '/subscribers', icon: UserCheck, label: 'Subscribers', roles: ['superadmin','distributor','lco'] },
  { path: '/subscriptions', icon: CreditCard, label: 'Subscriptions', roles: ['superadmin'] },
  { path: '/wallet', icon: Wallet, label: 'Wallet', roles: ['superadmin','distributor','lco'] },
  { path: '/network', icon: Shield, label: 'Network Lock', roles: ['superadmin'] },
  { path: '/settings', icon: Settings, label: 'Settings', roles: ['superadmin','distributor','lco'] },
];

const Layout = () => {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const [collapsed, setCollapsed] = useState(false);

  const navItems = allNavItems.filter(item => item.roles.includes(user?.role));

  const handleLogout = () => {
    logout();
    navigate('/login');
  };

  const roleColor = {
    superadmin: 'var(--accent-red)',
    distributor: 'var(--accent-orange)',
    lco: 'var(--accent-blue)',
    enduser: 'var(--accent-teal)'
  };

  return (
    <div style={{ display: 'flex', minHeight: '100vh', background: 'var(--bg-primary)' }}>
      {/* Sidebar */}
      <div style={{
        width: collapsed ? '70px' : '240px',
        background: 'var(--bg-sidebar)',
        borderRight: '1px solid var(--border)',
        display: 'flex',
        flexDirection: 'column',
        transition: 'width 0.3s ease',
        position: 'fixed',
        top: 0, left: 0, bottom: 0,
        zIndex: 100,
        overflow: 'hidden'
      }}>
        {/* Logo */}
        <div style={{
          padding: '20px 16px',
          borderBottom: '1px solid var(--border)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          minHeight: '70px'
        }}>
          {!collapsed && (
            <div>
              <div style={{ fontSize: '20px', fontWeight: '700', color: 'var(--accent-blue)', letterSpacing: '1px' }}>
                NOVA<span style={{ color: 'var(--accent-teal)' }}>CAST</span>
              </div>
              <div style={{ fontSize: '10px', color: 'var(--text-secondary)', marginTop: '2px', letterSpacing: '2px' }}>
                IPTV PLATFORM
              </div>
            </div>
          )}
          {collapsed && (
            <div style={{ fontSize: '18px', fontWeight: '700', color: 'var(--accent-blue)', margin: '0 auto' }}>N</div>
          )}
          <button onClick={() => setCollapsed(!collapsed)}
            style={{ background: 'none', border: 'none', color: 'var(--text-secondary)', cursor: 'pointer', padding: '4px' }}>
            {collapsed ? <Menu size={18} /> : <X size={18} />}
          </button>
        </div>

        {/* Nav Items */}
        <nav style={{ flex: 1, padding: '12px 8px', overflowY: 'auto' }}>
          {navItems.map((item) => (
            <NavLink
              key={item.path}
              to={item.path}
              end={item.exact}
              style={({ isActive }) => ({
                display: 'flex',
                alignItems: 'center',
                gap: '12px',
                padding: '11px 12px',
                borderRadius: '8px',
                marginBottom: '4px',
                textDecoration: 'none',
                color: isActive ? 'var(--accent-blue)' : 'var(--text-secondary)',
                background: isActive ? 'rgba(79,110,247,0.12)' : 'transparent',
                borderLeft: isActive ? '3px solid var(--accent-blue)' : '3px solid transparent',
                transition: 'all 0.2s',
                whiteSpace: 'nowrap',
                overflow: 'hidden'
              })}
            >
              <item.icon size={18} style={{ flexShrink: 0 }} />
              {!collapsed && (
                <>
                  <span style={{ fontSize: '14px', fontWeight: '500', flex: 1 }}>{item.label}</span>
                  <ChevronRight size={14} style={{ opacity: 0.4 }} />
                </>
              )}
            </NavLink>
          ))}
        </nav>

        {/* User Info */}
        <div style={{ padding: '16px', borderTop: '1px solid var(--border)' }}>
          {!collapsed && (
            <div style={{ marginBottom: '12px' }}>
              <div style={{ fontSize: '13px', fontWeight: '600', color: 'var(--text-primary)' }}>
                {user?.full_name || user?.username}
              </div>
              <div style={{ fontSize: '11px', color: roleColor[user?.role], textTransform: 'uppercase', letterSpacing: '1px' }}>
                {user?.role}
              </div>
            </div>
          )}
          <button onClick={handleLogout} className="btn btn-secondary" style={{ width: '100%', justifyContent: 'center' }}>
            <LogOut size={16} />
            {!collapsed && 'Logout'}
          </button>
        </div>
      </div>

      {/* Main Content */}
      <div style={{
        marginLeft: collapsed ? '70px' : '240px',
        flex: 1,
        display: 'flex',
        flexDirection: 'column',
        transition: 'margin-left 0.3s ease',
        minHeight: '100vh'
      }}>
        {/* Top Bar */}
        <div style={{
          height: '70px',
          background: 'var(--bg-sidebar)',
          borderBottom: '1px solid var(--border)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          padding: '0 24px',
          position: 'sticky',
          top: 0,
          zIndex: 50
        }}>
          <div style={{ fontSize: '13px', color: 'var(--text-secondary)' }}>
            NovaCast — <span style={{ color: roleColor[user?.role], textTransform: 'capitalize', fontWeight: '500' }}>{user?.role}</span> Panel
          </div>
          <div style={{ display: 'flex', alignItems: 'center', gap: '16px' }}>
            <button style={{ background: 'none', border: 'none', color: 'var(--text-secondary)', cursor: 'pointer' }}>
              <Bell size={18} />
            </button>
            <div style={{
              width: '36px', height: '36px',
              background: roleColor[user?.role] || 'var(--accent-blue)',
              borderRadius: '50%',
              display: 'flex', alignItems: 'center', justifyContent: 'center',
              fontSize: '14px', fontWeight: '600', color: 'white'
            }}>
              {user?.username?.[0]?.toUpperCase()}
            </div>
          </div>
        </div>

        {/* Page Content */}
        <div style={{ padding: '24px', flex: 1 }}>
          <Outlet />
        </div>
      </div>
    </div>
  );
};

export default Layout;
